import { useTranslation } from "react-i18next";
import img1 from "../../../styles/images/1.svg";
import img2 from "../../../styles/images/2.svg";
import img3 from "../../../styles/images/3.svg";
import img4 from "../../../styles/images/4.svg";
import { Col, Row } from "antd";
import { HOME_WOW_DURATION, homeWowDelay } from "../../../utils/homeWow";
import { useSectionData, sectionText } from "./useSectionData";
import { pageBannerImage } from "../../../Components/layouts/usePageBanner";
import PageBannerLoading from "../../../Components/layouts/PageBannerLoading";

type CareSection = {
  title?: string | false;
  description?: string | false;
  items?: unknown;
};

type CareCard = { title: string; subtitle: string; img: string; remote: boolean };

const Care = () => {
  const { t } = useTranslation();
  const section = useSectionData<CareSection>("care-section");

  const staticData: CareCard[] = [img1, img2, img3, img4].map((img, i) => ({
    title: t(`home.care.items.item${i + 1}.title`),
    subtitle: t(`home.care.items.item${i + 1}.subtitle`),
    img,
    remote: false,
  }));

  // An item is only usable if it has a title; icon and text fall back one by one.
  const apiData: CareCard[] = (Array.isArray(section?.items) ? section!.items : [])
    .map((item: any, i) => {
      const title = sectionText(item?.title);
      if (!title) return null;
      const icon = pageBannerImage(item?.icon_url);
      return {
        title,
        subtitle: sectionText(item?.description) ?? "",
        img: icon ?? staticData[i % staticData.length].img,
        remote: Boolean(icon),
      };
    })
    .filter((card): card is CareCard => card !== null);

  const data = apiData.length ? apiData : staticData;
  const heading = sectionText(section?.title) ?? t("home.care.title");
  const subtitle = sectionText(section?.description) ?? t("home.care.subtitle");

  if (section === undefined) return <PageBannerLoading />;

  return (
    <section className="section-box box-why-book-22 background-body pt-20 ac-home-care">
      <div className="container">
        <div
          className="ac-home-care__intro wow fadeInUp"
          data-wow-duration={HOME_WOW_DURATION}
          data-wow-delay={homeWowDelay(0)}
        >
          <h3 className="heading-3 mb-2 neutral-1000 ac-home-care__heading ac--font-expo">
            {heading}
          </h3>

          <p className="text-lg-medium neutral-500 ac-home-care__subtitle ac--font-expo">
            {subtitle}
          </p>
        </div>
        <Row className="ac-home-care__row mt-40" gutter={[20, 20]}>
          {data.map((element, index) => (
            <Col
              key={`${element.title}-${index}`}
              xxl={6}
              xl={6}
              md={12}
              lg={12}
              xs={12}
              sm={12}
            >
              <div
                className="card-why wow fadeIn ac-home-care__card"
                data-wow-duration={HOME_WOW_DURATION}
                data-wow-delay={homeWowDelay(1 + index)}
              >
                <div className="card-image ac-home-care__icon-wrap">
                  {element.remote ? (
                    // ponytail: uploaded icons keep their own colours, the
                    // bundled SVGs stay tinted through the CSS mask.
                    <img
                      className="ac-home-care__icon-img"
                      src={element.img}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <span
                      className="ac-home-care__icon"
                      aria-hidden="true"
                      style={{
                        WebkitMaskImage: `url("${element.img}")`,
                        maskImage: `url("${element.img}")`,
                      }}
                    />
                  )}
                </div>
                <div className="card-info ac-home-care__body">
                  <h6 className="text-xl-bold neutral-1000 ac-home-care__title ac--font-expo">
                    {element.title}
                  </h6>

                  {element.subtitle && (
                    <p className="text-md-medium neutral-500 ac-home-care__desc ac--font-expo">
                      {element.subtitle}
                    </p>
                  )}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

export default Care;
